import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { ShoppingCart, Trash2, Plus, Minus, ArrowLeft, ArrowRight, Truck } from 'lucide-react';
import axios from 'axios';
import { useState, useEffect } from 'react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartPage = () => {
  const { cart, removeFromCart, addToCart, fetchCart } = useCart();
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [freeShippingThreshold, setFreeShippingThreshold] = useState(75);

  useEffect(() => {
    // Fetch shipping methods to get free shipping threshold
    const fetchShippingInfo = async () => {
      try {
        const response = await axios.get(`${API_URL}/shipping/methods`);
        setFreeShippingThreshold(response.data.free_shipping_threshold);
      } catch (error) {
        console.error('Error fetching shipping info:', error);
      }
    };
    fetchShippingInfo();
  }, []);

  const handleRemove = async (productId) => {
    const success = await removeFromCart(productId);
    if (success) {
      toast.success('Item removed from cart');
    }
  };

  const handleQuantityChange = async (productId, currentQty, change) => {
    if (currentQty + change <= 0) {
      handleRemove(productId);
      return;
    }
    // For simplicity, just add/remove one item
    if (change > 0) {
      await addToCart(productId, 1);
    } else {
      // Remove and re-add with new quantity would be complex, so just remove for now
      handleRemove(productId);
    }
  };

  const handleProceedToCheckout = () => {
    if (cart.items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    navigate('/checkout');
  };

  const amountToFreeShipping = Math.max(0, freeShippingThreshold - cart.total);
  const qualifiesForFreeShipping = cart.total >= freeShippingThreshold;

  return (
    <div className="min-h-screen bg-cream-100 py-12" data-testid="cart-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          <h1 className="font-heading text-3xl font-bold text-royal-700">Shopping Cart</h1>
        </div>

        {cart.items.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-gray-600 mb-2">Your cart is empty</h3>
              <p className="text-gray-500 mb-6">Explore our shop and add some items!</p>
              <Button onClick={() => navigate('/shop')} className="btn-primary">
                Browse Shop
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.items.map((item) => (
                <Card key={item.product_id} className="overflow-hidden" data-testid={`cart-item-${item.product_id}`}>
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={item.product?.image_url || `https://via.placeholder.com/96x96?text=${encodeURIComponent(item.product?.name || 'Product')}`}
                          alt={item.product?.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 mb-1">{item.product?.name}</h3>
                        <p className="text-gray-500 text-sm mb-2">{item.product?.category}</p>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="icon"
                              className="w-8 h-8"
                              onClick={() => handleQuantityChange(item.product_id, item.quantity, -1)}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="w-8 text-center font-medium">{item.quantity}</span>
                            <Button
                              variant="outline"
                              size="icon"
                              className="w-8 h-8"
                              onClick={() => handleQuantityChange(item.product_id, item.quantity, 1)}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          <div className="text-right">
                            <p className="text-lg font-bold text-saffron-600">
                              ${item.item_total?.toFixed(2)}
                            </p>
                            <p className="text-xs text-gray-500">
                              ${item.product?.price?.toFixed(2)} each
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleRemove(item.product_id)}
                        data-testid={`remove-${item.product_id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <Card className="sticky top-24">
                <CardHeader>
                  <CardTitle className="font-heading text-xl">Order Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal ({cart.items.reduce((acc, item) => acc + item.quantity, 0)} items)</span>
                    <span>${cart.total?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Shipping</span>
                    <span className="text-gray-400">Calculated at checkout</span>
                  </div>
                  
                  {!qualifiesForFreeShipping && amountToFreeShipping > 0 && (
                    <div className="bg-saffron-50 border border-saffron-200 rounded-lg p-3">
                      <p className="text-saffron-800 text-sm flex items-center gap-2">
                        <Truck className="w-4 h-4" />
                        Add <span className="font-bold">${amountToFreeShipping.toFixed(2)}</span> more for FREE shipping!
                      </p>
                      <div className="mt-2 bg-saffron-100 rounded-full h-2">
                        <div 
                          className="bg-saffron-500 h-2 rounded-full transition-all"
                          style={{ width: `${Math.min(100, (cart.total / freeShippingThreshold) * 100)}%` }}
                        />
                      </div>
                    </div>
                  )}
                  
                  {qualifiesForFreeShipping && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-green-700 text-sm font-medium flex items-center gap-2">
                        <Truck className="w-4 h-4" />
                        You qualify for FREE standard shipping!
                      </p>
                    </div>
                  )}
                  
                  <Separator />
                  
                  <div className="flex justify-between text-lg font-bold">
                    <span>Estimated Total</span>
                    <span className="text-saffron-600">${cart.total?.toFixed(2)}</span>
                  </div>
                  
                  <Button
                    className="w-full btn-primary"
                    onClick={handleProceedToCheckout}
                    data-testid="checkout-button"
                  >
                    <ArrowRight className="w-5 h-5 mr-2" />
                    Proceed to Checkout
                  </Button>
                  
                  <p className="text-xs text-gray-500 text-center">
                    Shipping options available at checkout
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPage;
